import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, initMultiMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { aggregatingFetch } from "../common/hooks/networkHook";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = ticket => ticket.id

export const ticketSlice = createSlice({
    name: 'tickets',
    initialState: {
        data: {},
        managers: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(ticket => state.data[key(ticket)] = ticket);
            ensureArray(action.payload.manager).forEach(m => state.managers[m.username] = m);
         },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(t => state.data[key(t)] = t)
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(ticket => delete state.data[key(ticket)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => ticketSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'ticket', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'ticket');

const fetchTickets = aggregatingFetch(
    id => network => network.get('/api/v1/ticket/' + encodeURIComponent(id), data => ticketSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.get('/api/v1/ticket/', Array.from(ids), data => ticketSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
);

const fetchAllTicketManagers = network => network.get('/api/v2/ticketManagers', manager => ticketSlice.actions.set({manager}), visualizeFailure('get'));


export const createTicket = (network, ticket) => network.post('/api/v1/ticket', ticket, ticketSlice.actions.addOrUpdate,  visualizeFailure('create'));
export const updateTicket = (network, delta) => network.put('/api/v1/ticket' , delta, ticketSlice.actions.addOrUpdate,visualizeFailure('update'));
// #endregion

// #region selectors
const selectTicketData = state => state.tickets.data;
const selectTicket = id => createSelector([selectTicketData], tickets => tickets[id]);
const selectTickets = idsInput => {
    const ids = ensureArray(idsInput);
    return createSelector([selectTicketData], tickets => tickets.filter(ticket => ids.includes(ticket.id)));
}
const selectTicketManagersData = state => state.tickets.managers;
const selectTicketManagers = createSelector([selectTicketManagersData], (ticketManagers) => Object.values(ticketManagers));

// #endregion

const listen = {
    ticketCreated: (ticket, { dispatch, websocket }) => {
        markFetched(websocket, 'ticket', ticket.id)
        dispatch(ticketSlice.actions.addOrUpdate(ticket))
    },
    ticketUpdated: (ticket, { dispatch }) => dispatch(ticketSlice.actions.addOrUpdate(ticket)),
    ticketRemoved: (ticket, { dispatch }) => dispatch(ticketSlice.actions.remove(ticket)) 
}

// have to use the same factory for the single and multiple gets so that single and multiple deps are unified
const ticketInitByID = initMapFactory(fetchTickets, listen);
registerEntityBasedFetch('ticket', ticketInitByID);

const cleanup = { root: ticketSlice.name, all: state => Object.values(selectTicketData(state)), removeAll: ticketSlice.actions.remove, key: ticket => ticket.id };
export const [useTicket] = dataHookFactory(selectTicket, ticketInitByID, { cleanup });
export const [useTickets] = dataHookFactory(selectTickets, initMultiMapFactory(ticketInitByID, listen), { cleanup });
export const [useTicketManagers] = dataHookFactory(selectTicketManagers, { run: fetchAllTicketManagers, listen });

export const canRouteTickets = ({credentials, clientConfig}) => credentials.hasRole(['administrator', 'ticket manager', 'auditor', 'ticket auditor', 'member']) && clientConfig.enableTickets

// WARNING: do not replace the use of this constant with the function invocation - doing so will break the singleton pattern required by factories
// consult on any changes to the below since we are doing an explicit argument drop on the call chain

const reducer = { name: ticketSlice.name, reducer: ticketSlice.reducer }
export default reducer;